// SAM-1095 — form models for the full-page Article and Ad editors, and what each one sends to the API.
// Dates are calendar days (YYYY-MM-DD). Status is only ever 'draft' or 'published'; Scheduled and Expired are
// worked out from the dates (utils/cmsState.ts).
import type { Article } from '../../hooks/useArticles';
import type { Ad } from '../../hooks/useAds';
import { authorDisplayName } from '../../hooks/useArticles';
import { dayOf } from '../../utils/cmsState';
import { SEMICOLON_MESSAGE, cleanCrudText, hasSemicolon } from '../../utils/crudText';

export type LinkAction = '' | 'url' | 'business' | 'article';

export interface ArticleForm {
  title_en: string;
  title_es: string;
  content_en: string;
  content_es: string;
  art_img_url: string;
  status: 'draft' | 'published';
  author_id: number;
  article_type_id: number;
  publish_date: string;
  expiration_date: string;
  user_types_ids: number[];
  link_action: LinkAction;
  link_id: string;
}

export interface AdForm {
  title_en: string;
  title_es: string;
  short_description_en: string;
  short_description_es: string;
  art_img_url: string;
  background_color: string;
  status: 'draft' | 'published';
  priority: number;
  publish_date: string;
  expiration_date: string;
  user_types_ids: number[];
  link_action: LinkAction;
  link_id: string;
}

/** The ad card shows a 2-line title; the mock counts 40 characters. */
export const AD_TITLE_MAX = 40;
/** About how many characters of the short text fit in the two lines of the card. */
export const AD_TEXT_FIT = 70;

const asLinkAction = (v?: string): LinkAction => (v === 'url' || v === 'business' || v === 'article' ? v : '');

export const EMPTY_ARTICLE_FORM: ArticleForm = {
  title_en: '', title_es: '', content_en: '', content_es: '', art_img_url: '', status: 'draft',
  author_id: 0, article_type_id: 0, publish_date: '', expiration_date: '', user_types_ids: [], link_action: '', link_id: '',
};

export const EMPTY_AD_FORM: AdForm = {
  title_en: '', title_es: '', short_description_en: '', short_description_es: '', art_img_url: '', background_color: '',
  status: 'draft', priority: 0, publish_date: '', expiration_date: '', user_types_ids: [], link_action: '', link_id: '',
};

export const articleToForm = (a: Article): ArticleForm => ({
  title_en: a.title_en || '',
  title_es: a.title_es || '',
  content_en: a.content_en || '',
  content_es: a.content_es || '',
  art_img_url: a.art_img_url || '',
  status: a.status === 'published' ? 'published' : 'draft',
  author_id: a.author_id || 0,
  article_type_id: a.article_type_id || 0,
  publish_date: dayOf(a.publish_date) || '',
  expiration_date: dayOf(a.expiration_date) || '',
  user_types_ids: a.user_types_ids || [],
  link_action: asLinkAction(a.action_link_object?.action),
  link_id: a.action_link_object?.id != null ? String(a.action_link_object.id) : '',
});

export const adToForm = (a: Ad): AdForm => ({
  title_en: a.title_en || '',
  title_es: a.title_es || '',
  short_description_en: a.short_description_en || '',
  short_description_es: a.short_description_es || '',
  art_img_url: a.art_img_url || '',
  background_color: a.background_color || '',
  status: a.status === 'published' ? 'published' : 'draft',
  priority: a.priority || 0,
  publish_date: dayOf(a.publish_date) || '',
  expiration_date: dayOf(a.expiration_date) || '',
  user_types_ids: a.user_types_ids || [],
  link_action: asLinkAction(a.action_link_object?.action),
  link_id: a.action_link_object?.id != null ? String(a.action_link_object.id) : '',
});

const linkObject = (f: { link_action: LinkAction; link_id: string }) =>
  f.link_action ? { action: f.link_action, id: f.link_id.trim() || undefined } : null;

// SAM-127: user_types_ids goes as a real integer array, never a Postgres array literal.
const userTypes = (ids: number[]) => ids.map((id) => Number(id)).filter((id) => Number.isInteger(id));

export const articlePayload = (f: ArticleForm, status: 'draft' | 'published'): Partial<Article> => ({
  title_en: cleanCrudText(f.title_en),
  title_es: cleanCrudText(f.title_es),
  content_en: cleanCrudText(f.content_en),
  content_es: cleanCrudText(f.content_es),
  art_img_url: f.art_img_url || undefined,
  status,
  author_id: f.author_id,
  article_type_id: f.article_type_id,
  publish_date: f.publish_date || undefined,
  expiration_date: f.expiration_date || undefined,
  user_types_ids: userTypes(f.user_types_ids),
  action_link_object: linkObject(f),
});

export const adPayload = (f: AdForm, status: 'draft' | 'published'): Partial<Ad> => ({
  title_en: cleanCrudText(f.title_en),
  title_es: cleanCrudText(f.title_es),
  short_description_en: cleanCrudText(f.short_description_en) || undefined,
  short_description_es: cleanCrudText(f.short_description_es) || undefined,
  art_img_url: f.art_img_url || undefined,
  background_color: f.background_color || undefined,
  action_link_object: linkObject(f),
  status,
  priority: f.priority,
  publish_date: f.publish_date || undefined,
  expiration_date: f.expiration_date || undefined,
  user_types_ids: userTypes(f.user_types_ids),
});

export interface Missing { field: string; message: string }

const datesProblem = (f: { publish_date: string; expiration_date: string }): Missing | null =>
  f.publish_date && f.expiration_date && f.expiration_date < f.publish_date
    ? { field: 'expiration_date', message: 'Expires must be on or after Publishes' }
    : null;

/** Text fields with a ; (crud refuses it), for both languages. */
const semicolons = (f: object, keys: string[]): Missing[] =>
  keys.filter((k) => hasSemicolon((f as Record<string, string>)[k])).map((k) => ({ field: k, message: SEMICOLON_MESSAGE }));

const ARTICLE_TEXT = ['title_en', 'title_es', 'content_en', 'content_es'];
const AD_TEXT = ['title_en', 'title_es', 'short_description_en', 'short_description_es'];

/** A draft only needs an English title; publishing needs everything the app shows. */
export const articleMissing = (f: ArticleForm, publishing: boolean): Missing[] => {
  const out: Missing[] = [];
  if (!f.title_en.trim()) out.push({ field: 'title_en', message: 'Title is required' });
  if (!f.author_id) out.push({ field: 'author_id', message: 'Author is required' });
  if (publishing) {
    if (!f.content_en.trim()) out.push({ field: 'content_en', message: 'Content is required' });
    if (!f.title_es.trim() || !f.content_es.trim()) out.push({ field: 'title_es', message: 'Español is empty: wait for the translation or write it' });
    if (!f.article_type_id) out.push({ field: 'article_type_id', message: 'Type is required' });
    if (!f.publish_date) out.push({ field: 'publish_date', message: 'Publishes is required' });
    if (!f.expiration_date) out.push({ field: 'expiration_date', message: 'Expires is required: without it the article never leaves the app' });
  }
  const dp = datesProblem(f);
  if (dp) out.push(dp);
  out.push(...semicolons(f, ARTICLE_TEXT));
  return out;
};

export const adMissing = (f: AdForm, publishing: boolean): Missing[] => {
  const out: Missing[] = [];
  if (!f.title_en.trim()) out.push({ field: 'title_en', message: 'Title is required' });
  if (f.title_en.length > AD_TITLE_MAX) out.push({ field: 'title_en', message: `Title is over ${AD_TITLE_MAX} characters` });
  if (publishing) {
    if (!f.title_es.trim()) out.push({ field: 'title_es', message: 'Español is empty: wait for the translation or write it' });
    if (!f.art_img_url) out.push({ field: 'art_img_url', message: 'Image is required' });
    if (!f.publish_date) out.push({ field: 'publish_date', message: 'Publishes is required' });
    if (!f.expiration_date) out.push({ field: 'expiration_date', message: 'Expires is required: without it the ad never leaves the carousel' });
  }
  const dp = datesProblem(f);
  if (dp) out.push(dp);
  out.push(...semicolons(f, AD_TEXT));
  return out;
};

/**
 * The author a new article starts with: the one named like the tenant brand, else the only one, else none.
 * Authors are global until SAM-1099 filters them by tenant, so never guess among several.
 */
export const defaultAuthorId = (authors: { id: number; first_name?: string; last_name?: string; name?: string }[], brand?: string | null): number => {
  const b = (brand || '').trim().toLowerCase();
  if (b) {
    const hit = authors.find((a) => authorDisplayName(a).toLowerCase().includes(b));
    if (hit) return hit.id;
  }
  return authors.length === 1 ? authors[0].id : 0;
};

/** Next free carousel position for a new ad. */
export const nextPriority = (ads: Pick<Ad, 'priority'>[]): number => Math.max(0, ...ads.map((a) => a.priority || 0)) + 1;

/**
 * The real last place of the carousel: one after the highest priority among the ads that are in the app. An ad that
 * is not live yet (draft, scheduled) is written there too, so publishing it later never puts it first by accident.
 */
export const endOfCarousel = (liveAds: Pick<Ad, 'priority'>[]): number => nextPriority(liveAds);

/** Adds `days` to a calendar day (YYYY-MM-DD). */
export const addDays = (day: string, days: number): string => {
  const [y, m, d] = day.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + days));
  return t.toISOString().slice(0, 10);
};
